/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * The event page's own buttons, decided here rather than read from admin.
 *
 * These four used to come from the public view's stored `action_buttons`, with
 * the server re-checking each `visible_when` before honouring a press. The page
 * owns them now: same four ids, same labels, same destinations, same order —
 * built from the event record and the registration rules instead of from
 * configuration.
 *
 * SAME SHAPE AS resolveActionButtons ON PURPOSE. EventDetailTemplate and
 * EventActionButton are unchanged and know nothing about where a button came
 * from, so the two sources stay interchangeable and this can be reverted by
 * swapping one call.
 *
 * WHAT THIS GIVES UP, stated plainly because it is the point of the change. The
 * server no longer re-checks whether a button may be pressed. Hiding a button
 * is presentation; it was the server's check that made a closed window or an
 * invitation-only event impossible to bypass by calling the API directly. These
 * rules are now the product's intent rather than its enforcement — which is why
 * registrationRules fails closed on anything it does not recognise.
 *
 * STILL THE SERVER'S, and deliberately not reproduced here:
 *   • `event.status` — moved by scheduled transitions on a clock.
 *   • Capacity — a row limit checked inside the write.
 */

import { pageHref } from '../../../utils/pageHref';
import { isSafeUrl } from '../../../utils/templateSanitiser';
import { registrationRules } from './registrationRules';
import { checkInOpensHint } from './selfCheckInRules';

/** Statuses that mean entry is over rather than not yet begun. */
const CLOSED_STATUSES = ['REGISTRATION_CLOSED', 'COMPLETED', 'CANCELLED'];

/** Where "Entries have closed" sends somebody — back to the event list. */
const EVENT_LIST_PATH = '/p/event';

/** The Page Builder page that lists what this visitor has entered. */
const MY_REGISTRATIONS_SLUG = 'my-registrations';

/**
 * The buttons for one event, in the order they are drawn.
 *
 * `rules` is optional: passing the already-computed rules avoids deriving them
 * twice on a page that needs them for the entry modal anyway.
 */
export function eventButtons(event, scope, rules = null, { entered = false } = {}) {
  const record = event || {};
  const decided = rules || registrationRules(record, scope);
  const buttons = [];

  // 1. Register — the only button that writes. It opens the entry modal rather
  //    than submitting, because which activities and how many seats are still
  //    unanswered at this point.
  //
  //    ALSO WHEN ENTRIES HAVE CLOSED, for somebody who already entered. The
  //    button never shows as "Register" then — it is only drawn in its
  //    completed state — but it is what carries "You are registered" and the
  //    check-in line, and without it both vanished the moment registration
  //    closed, which is exactly when somebody comes back to check in.
  if (decided.canRegister || entered) {
    // When they can check themselves in, and that it happens in My
    // Registrations. See checkInOpensHint.
    const checkInHint = checkInOpensHint(record, new Date());
    buttons.push({
      id: 'register',
      type: 'submit',
      label: 'Register for this event',
      icon: '',
      style: 'primary',
      href: null,
      openInNewTab: false,
      action: {
        type: 'submit',
        // What stands in the button's place once this visitor has entered.
        // Without it completedMessage falls back to "You have already
        // completed this", which tells somebody nothing about their entry.
        on_completed: {
          hide: true,
          // Followed by when they can check themselves in, while that is
          // still ahead. See checkInOpensHint.
          message: ['You are registered for this event.', checkInHint].filter(Boolean).join(' '),
          // The same line on its own, for the phone dock, which has no room
          // for the rail's sentence but must not lose the check-in time.
          check_in_hint: checkInHint,
        },
      },
      order: 0,
    });
  }

  // 2. The organiser's own page for this event. Only when they gave one, and
  //    only when it survives the scheme allowlist: this is a value from a
  //    record, so it is checked here exactly as the configured path checked it.
  const organiserUrl = record.official_website_url;
  if (organiserUrl && isSafeUrl(String(organiserUrl))) {
    buttons.push({
      id: 'organiser_site',
      type: 'redirect',
      label: 'Visit organiser website',
      icon: '',
      style: 'secondary',
      href: String(organiserUrl),
      openInNewTab: true,
      action: { type: 'redirect', target: 'url' },
      order: 1,
    });
  }

  // 3. Entries have closed. Says so rather than leaving the band empty, which
  //    reads as a page that failed to load its button.
  if (CLOSED_STATUSES.includes(record.status)) {
    buttons.push({
      id: 'closed_notice',
      type: 'redirect',
      label: 'Entries have closed',
      icon: '',
      style: 'secondary',
      href: EVENT_LIST_PATH,
      openInNewTab: false,
      action: { type: 'redirect', target: 'url' },
      order: 2,
    });
  }

  // 4. Always offered. Somebody who has already entered arrives looking for
  //    their pass, and this is the way back to it.
  buttons.push({
    id: 'view_my_registrations',
    type: 'redirect',
    label: 'View my registrations',
    icon: '',
    style: 'filled',
    href: pageHref(MY_REGISTRATIONS_SLUG),
    openInNewTab: false,
    action: { type: 'redirect', target: 'page', page_slug: MY_REGISTRATIONS_SLUG },
    order: 5,
  });

  return buttons.sort((a, b) => a.order - b.order);
}
